import { isMock } from "@/mocks";
import { Avatar, CHARACTER_TYPE } from "@vixi/types";
import { useMutation } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useState } from "react";
import { CreateAvatarResponse, GenerateAvatarRequest } from "./useGenerateCourseAvatar";
import _ from "lodash";
import api, { assetUrl, endpoints, formdataFromObj } from "@vixi/api";
import { sleepAsync } from "@vixi/utils";

const mockAvatars = [
  "/Attachments/avatars/c9fd64df-d5e9-469c-a8bc-b622a956edbf/56a46453-2cd7-4f99-9771-c5fab91ae192.jpg",
  "/Attachments/avatars/9dd784b9-8620-4afd-be7b-9e7e6d0f9d35/75a2a71d-1b00-4a17-8614-1490741bf1eb.jpg",
  "/Attachments/avatars/3b110129-e9d2-4d6b-8c23-143760a49c42/12aac385-e95d-4613-b298-83563c9206fc.jpg",
  "/Attachments/avatars/2faafa6b-c955-4944-b420-0ad888525edf/27a5a117-3118-423f-9c6b-843ef1197532.jpg",
  "/Attachments/avatars/9f527374-2ea6-417f-8012-2d1a2e8cb991/2d3c1293-f07c-4782-ae2a-6e2e66faec98.jpg",
  "/Attachments/avatars/a691eb3e-e9fa-4cc9-8047-0844e9ae42af/a63fe907-58a1-4b71-b3e9-5ce1d0be0da9.jpg",
] as const;

export type GenerateAvatarData = Omit<GenerateAvatarRequest, "purpose">;

const mockCreateAvatar =
  (setUploadProgress: Dispatch<SetStateAction<number>>) =>
  async (_data: GenerateAvatarData): Promise<Avatar[]> => {
    setUploadProgress(20);

    await sleepAsync(1000);

    setUploadProgress(100);

    return [
      {
        url: assetUrl(_.sample(mockAvatars)),
        characterType: CHARACTER_TYPE.COURSE_CHARACTER,
      },
    ];
  };

const createAvatar =
  (setUploadProgress: Dispatch<SetStateAction<number>>) => async (data: GenerateAvatarData) => {
    const response = await api.post<CreateAvatarResponse>(
      endpoints.createAvatar,
      formdataFromObj({ ...data, purpose: "avatar" }),
      {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(percentCompleted);
        },
      },
    );

    response.data.url = assetUrl(response.data.url);

    return [response.data];
  };

const getMutationFn = isMock("createAvatar") ? mockCreateAvatar : createAvatar;

export const useCreateAvatar = () => {
  const [uploadProgress, setUploadProgress] = useState(0);

  const mutation = useMutation({
    mutationKey: ["generate-course-avatar"],
    mutationFn: getMutationFn(setUploadProgress),
  });

  return {
    ...mutation,
    uploadProgress,
  };
};
